import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { gamesAPI, userAPI } from '../services/api';
import { io, Socket } from 'socket.io-client';
import { useTranslation } from 'react-i18next';
import './GameRoom.css';
import '../styles/game-shell.css';
import ProfileAvatar from './ProfileAvatar';
import Modal from './common/Modal';

interface Game {
  id: string;
  name: string;
  hostId: string;
  hostName: string;
  type: 'public' | 'private' | 'friends';
  gameVariant: 'lower_green';
  status: 'waiting' | 'playing' | 'finished';
  maxPlayers: number;
  currentPlayers: number;
  decks: number;
  dealtCards: number;
  players: string[];
  spectators: string[];
  inviteCode?: string;
  createdAt: string;
}

interface Card {
  id: string;
  value: number;
  symbol: 'acorn' | 'ball' | 'heart' | 'leaf';
  image: string;
  isSpecial: boolean;
}

interface GamePlayer {
  id: string;
  name: string;
  cards: Card[];
  isDealer: boolean;
  isActive: boolean;
  position: number;
}

interface GameState {
  id: string;
  players: GamePlayer[];
  dealerIndex: number;
  currentPlayerIndex: number;
  faceUpDeck: Card[];
  faceDownDeck: Card[];
  cutCard: Card | null;
  gamePhase: 'waiting' | 'dealing' | 'playing' | 'finished' | 'match-finished' | 'game-over';
  winner: string | null;
  sevenChain: number;
  selectedSymbol: 'acorn' | 'ball' | 'heart' | 'leaf' | null;
  skipCount: number;
  lastPlayedUpperBishop: Card | null;
  gameVariant: 'lower_green';
  gameMode: 'friendly' | 'betting';
  entryFee: number;
  platformFee: number;
  playerCoins: { [playerId: string]: number };
  coinPayouts: { [playerId: string]: number };
  totalPot: number;
  playerScores: { [playerId: string]: number };
  currentMatch: number;
  matchWinner: string | null;
  playedCards: Card[];
}

const GameRoom: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  // State variables
  const [game, setGame] = useState<Game | null>(null);
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isInRoom, setIsInRoom] = useState(false);
  const [isSitting, setIsSitting] = useState(false);
  const [isSpectating, setIsSpectating] = useState(false);
  const [playerUsers, setPlayerUsers] = useState<{ [playerId: string]: any }>({});

  // Panel collapse state
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(isMobile);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(isMobile);

  // Special game states
  const [showSymbolSelector, setShowSymbolSelector] = useState(false);
  const [canInterceptAce, setCanInterceptAce] = useState(false);
  const [showAceInterceptPrompt, setShowAceInterceptPrompt] = useState(false);
  const [showInterceptionPrompt, setShowInterceptionPrompt] = useState(false);
  const [pendingInterception, setPendingInterception] = useState<string | null>(null);

  const currentPlayerCards = gameState?.players.find(p => p.id === user?.id)?.cards || [];
  const currentPlayer = gameState?.players.find(p => p.id === user?.id);
  const isMyTurn = currentPlayer?.isActive || false;
  const isHost = game?.hostId === user?.id;
  const maxPlayersForGame = game?.maxPlayers || 6;

  // Always call useEffect unconditionally, after isMyTurn is defined
  useEffect(() => {
    if (gameState && isMyTurn && isSitting) {
      checkCanInterceptAce();
    }
  }, [gameState, isMyTurn, isSitting]);

  useEffect(() => {
    if (!gameId || !user) return;

    loadGame();
    initializeSocket();

    return () => {
      if (socket) {
        socket.close();
      }
    };
  }, [gameId, user]);

  // Request game state when socket is available and game is playing
  useEffect(() => {
    if (socket && game && game.status === 'playing' && game.players.includes(user!.id)) {
      console.log('Socket available and game is playing, requesting game state');
      socket.emit('getGameState', { gameId });
    }
  }, [socket, game, user]);

  const loadGame = async () => {
    try {
      setLoading(true);
      const response = await gamesAPI.getById(gameId!);
      const gameData = response.data;
      setGame(gameData);

      setIsInRoom(gameData.players.includes(user!.id) || gameData.spectators.includes(user!.id));
      setIsSitting(gameData.players.includes(user!.id));
      setIsSpectating(gameData.spectators.includes(user!.id));

      // Load user data for all players to get profile pictures
      await loadPlayerUsers(gameData.players);

      // If game is playing and user is sitting or spectating, request game state
      if (gameData.status === 'playing' && (gameData.players.includes(user!.id) || gameData.spectators.includes(user!.id)) && socket) {
        console.log('Game is playing and user is participating, requesting game state');
        socket.emit('getGameState', { gameId });
      }

      setError('');
    } catch (error) {
      console.error('Error loading game:', error);
      setError('Failed to load game');
    } finally {
      setLoading(false);
    }
  };

  const loadPlayerUsers = async (playerIds: string[]) => {
    try {
      const userPromises = playerIds.map(async (playerId) => {
        try {
          const response = await userAPI.getById(playerId);
          return { [playerId]: response.data };
        } catch (error) {
          console.error(`Error loading user data for ${playerId}:`, error);
        }
        return null;
      });

      const userResults = await Promise.all(userPromises);
      const newPlayerUsers = {};
      userResults.forEach(result => {
        if (result) {
          Object.assign(newPlayerUsers, result);
        }
      });
      setPlayerUsers(newPlayerUsers);
    } catch (error) {
      console.error('Error loading player users:', error);
    }
  };

  const initializeSocket = () => {
    if (!user) return;

    const token = localStorage.getItem('token');
    if (token) {
      const newSocket = io('/', {
        auth: { token },
      });

      newSocket.on('connect', () => {
        console.log('GameRoom: Connected to socket');
        newSocket.emit('joinUserRoom', { userId: user.id });
        newSocket.emit('joinGameRoom', { gameId });

        // If game is already playing and user is participating, request game state
        if (game && game.status === 'playing' && (game.players.includes(user.id) || game.spectators.includes(user.id))) {
          console.log('Game is already playing and user is participating, requesting game state');
          newSocket.emit('getGameState', { gameId });
        }
      });

      newSocket.on('disconnect', () => {
        console.log('GameRoom: Disconnected from socket');
      });

      newSocket.on('connect_error', (error) => {
        console.error('GameRoom: Socket connection error:', error);
      });

      newSocket.on('gameUpdate', (data: any) => {
        console.log('Game update received:', data);
        if (data.gameId === gameId) {
          loadGame();
        }
      });

      newSocket.on('gameDeleted', (data: any) => {
        if (data.gameId === gameId) {
          addNotification('Game was deleted by the host', 'info');
          navigate('/dashboard');
        }
      });

      newSocket.on('gameStarted', (data: any) => {
        console.log('Game started event received:', data);
        if (data.gameId === gameId) {
          addNotification('Game has started!', 'success');
          loadGame();
        }
      });

      // Game logic events
      newSocket.on('gameStateUpdated', (data: any) => {
        console.log('Game state updated:', data);
        if (data.gameId === gameId) {
          console.log('Setting game state:', data.gameState);
          console.log('Players:', data.gameState.players);
          console.log('Game phase:', data.gameState.gamePhase);
          console.log('Winner:', data.gameState.winner);
          setGameState(data.gameState);

          // Load user data for new players if any
          if (data.gameState.players) {
            const playerIds = data.gameState.players.map((p: any) => p.id);
            loadPlayerUsers(playerIds);
          }
        }
      });

      newSocket.on('gameStateReceived', (data: any) => {
        console.log('Game state received:', data);
        if (data.gameId === gameId) {
          console.log('Setting game state from received:', data.gameState);
          setGameState(data.gameState);
        }
      });

      newSocket.on('requireSymbolSelection', (data: any) => {
        console.log('Symbol selection required:', data);
        if (data.gameId === gameId) {
          setShowSymbolSelector(true);
        }
      });

      newSocket.on('canInterceptAceResponse', (data: any) => {
        console.log('Can intercept ace response:', data);
        if (data.gameId === gameId) {
          setCanInterceptAce(data.canIntercept);
          if (data.canIntercept) {
            setShowAceInterceptPrompt(true);
          }
        }
      });

      newSocket.on('requireInterception', (data: any) => {
        console.log('Interception required:', data);
        if (data.gameId === gameId) {
          setShowInterceptionPrompt(true);
          setPendingInterception(data.finishedPlayerId);
        }
      });

      newSocket.on('gameFinished', (data: any) => {
        console.log('Game finished:', data);
        if (data.gameId === gameId) {
          setGameState(data.gameState);
          addNotification(`Game finished! Winner: ${data.winner}`, 'success');
        }
      });

      newSocket.on('nextMatchStarted', (data: any) => {
        console.log('Next match started:', data);
        if (data.gameId === gameId) {
          setGameState(data.gameState);
          addNotification('Next match has started!', 'success');
        }
      });

      newSocket.on('gameRestarted', (data: any) => {
        console.log('Game restarted:', data);
        if (data.gameId === gameId) {
          setGameState(data.gameState);
          addNotification('Game has been restarted!', 'success');
        }
      });

      newSocket.on('gameError', (data: any) => {
        console.error('Game error:', data);
        addNotification(data.message, 'error');
      });

      // Card dealing animation events
      newSocket.on('cardDealt', (data: any) => {
        console.log('Card dealt:', data);
        if (data.gameId === gameId) {
          handleCardDealt(data);
        }
      });

      newSocket.on('dealingComplete', (data: any) => {
        console.log('Dealing complete:', data);
        if (data.gameId === gameId) {
          setGameState(data.gameState);
          addNotification('Cards have been dealt!', 'success');
        }
      });

      setSocket(newSocket);
    }
  };

  const addNotification = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    console.log(`${type.toUpperCase()}: ${message}`);
  };

  const handleCardDealt = (data: any) => {
    const { playerIndex, playerId, card, isLastCard } = data;

    if (!gameState) return;

    // Update game state for this player
    setGameState(prevState => {
      if (!prevState) return prevState;
      const updatedPlayers = [...prevState.players];
      const playerToUpdate = updatedPlayers[playerIndex];
      if (playerToUpdate && !playerToUpdate.cards.find(c => c.id === card.id)) {
        playerToUpdate.cards.push(card);
      }
      return { ...prevState, players: updatedPlayers };
    });
  };

  const getPlayerPositionsForAnimation = () => {
    const center = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const radius = 200;
    const positions = [];
    const playerCount = gameState?.players.length || 4;

    for (let i = 0; i < playerCount; i++) {
      const angle = (i * 2 * Math.PI) / playerCount - Math.PI / 2; // Start from top
      positions.push({
        x: center.x + radius * Math.cos(angle),
        y: center.y + radius * Math.sin(angle)
      });
    }

    return positions;
  };

  const handleJoinRoom = async (asSpectator: boolean = false) => {
    try {
      await gamesAPI.join(gameId!, asSpectator);
      setIsInRoom(true);
      setIsSpectating(asSpectator);
      setIsSitting(false);
      loadGame();
    } catch (error: any) {
      console.error('Error joining room:', error);

      // Handle 409 Conflict errors specifically
      if (error.response?.status === 409) {
        const errorMessage = error.response?.data?.message || 'Conflict when joining game';

        if (errorMessage.includes('already in the game')) {
          // User is already in the game - refresh the game state
          console.log('User already in game, refreshing state');
          loadGame();
          return;
        }

        addNotification(errorMessage, 'error');
      } else {
        addNotification('Failed to join room', 'error');
      }
    }
  };

  const handleSit = async () => {
    if (!gameId) {
      addNotification('Game ID not found', 'error');
      return;
    }

    try {
      console.log('Attempting to sit at table for game:', gameId);
      const response = await gamesAPI.sit(gameId);
      console.log('Sit response:', response.data);

      setIsSitting(true);
      setIsSpectating(false);
      addNotification('Successfully sat at table!', 'success');

      // Reload game state
      await loadGame();

      // Emit socket event to join room if not already in room
      if (socket && !isInRoom) {
        socket.emit('joinRoom', { gameId, asSpectator: false });
      }
    } catch (error: any) {
      console.error('Error sitting at table:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to sit at table';
      addNotification(errorMessage, 'error');
    }
  };

  const handleLeaveRoom = async () => {
    const isActiveGame = game?.status === 'playing';
    const msg = isActiveGame
      ? t('game.confirmLeaveResign')
      : t('game.confirmLeave');
    if (!window.confirm(msg)) return;

    try {
      await gamesAPI.leave(gameId!, isActiveGame);
      setIsInRoom(false);
      setIsSitting(false);
      setIsSpectating(false);
      // Just redirect to dashboard, don't reload game
      navigate('/dashboard');
    } catch (error) {
      console.error('Error leaving room:', error);
      addNotification('Failed to leave room', 'error');
    }
  };

  const handleStartGame = async () => {
    try {
      await gamesAPI.start(gameId!);
      addNotification('Game started!', 'success');
    } catch (error) {
      console.error('Error starting game:', error);
      addNotification('Failed to start game', 'error');
    }
  };

  const handleDeleteGame = async () => {
    if (window.confirm(t('game.confirmDelete'))) {
      try {
        await gamesAPI.delete(gameId!);
        addNotification('Game deleted', 'success');
        navigate('/dashboard');
      } catch (error) {
        console.error('Error deleting game:', error);
        addNotification('Failed to delete game', 'error');
      }
    }
  };

  const handleStartNextMatch = async () => {
    if (!socket || !gameId) return;

    console.log('Starting next match');
    socket.emit('startNextMatch', {
      gameId
    });
  };

  const handleRestartGame = async () => {
    if (!socket || !gameId) return;

    console.log('Restarting game');
    socket.emit('restartGame', {
      gameId
    });
  };

  // Initialize game logic only when game starts
  useEffect(() => {
    console.log('Game status changed:', game?.status, 'Game state:', gameState);
    if (game && game.status === 'playing' && !gameState) {
      console.log('Requesting game state for game:', game.id);
      // Request game state from server
      if (socket) {
        console.log('Socket connected, emitting getGameState event');
        socket.emit('getGameState', { gameId: game.id });

        // Retry after 2 seconds if no response
        const timeout = setTimeout(() => {
          if (!gameState) {
            console.log('Retrying game state request...');
            socket.emit('getGameState', { gameId: game.id });
          }
        }, 2000);

        return () => clearTimeout(timeout);
      } else {
        console.log('Socket not connected, cannot request game state');
      }
    }
  }, [game, gameState, socket]);



  // Game play functions (only used when game is playing)
  const playCard = (cardIndex: number) => {
    if (!socket || !gameId) {
      console.log('Cannot play card: socket or gameId missing', { socket: !!socket, gameId });
      return;
    }

    const card = currentPlayerCards[cardIndex];
    console.log('Playing card:', {
      cardIndex,
      cardId: card?.id,
      cardSymbol: card?.symbol,
      cardValue: card?.value,
      isMyTurn,
      isSitting,
      currentPlayerCards: currentPlayerCards.length,
      playableCards,
      isPlayable: playableCards.includes(cardIndex)
    });

    if (!isMyTurn || !isSitting) {
      console.log('Cannot play card: not your turn or not sitting');
      return;
    }

    if (!playableCards.includes(cardIndex)) {
      console.log('Cannot play card: not in playable cards list');
      return;
    }

    console.log('Emitting playCard event:', { gameId, cardIndex });
    socket.emit('playCard', {
      gameId,
      cardIndex
    });
  };

  const drawCard = () => {
    if (!socket || !gameId) {
      console.log('Draw card failed: socket or gameId missing', { socket: !!socket, gameId });
      return;
    }

    console.log('Drawing card', { gameId, isMyTurn, isSitting });
    socket.emit('drawCard', {
      gameId
    });
  };

  const selectSymbol = (symbol: 'acorn' | 'ball' | 'heart' | 'leaf') => {
    if (!socket || !gameId) return;

    console.log('Selecting symbol:', symbol);
    socket.emit('selectSymbol', {
      gameId,
      symbol
    });
    setShowSymbolSelector(false);
  };

  const interceptAce = (cardIndex: number) => {
    if (!socket || !gameId) return;

    console.log('Intercepting ace with card index:', cardIndex);
    socket.emit('interceptAce', {
      gameId,
      cardIndex
    });
    setShowAceInterceptPrompt(false);
  };

  const interceptLastCard = (cardIndex: number) => {
    if (!socket || !gameId) return;

    console.log('Intercepting last card with card index:', cardIndex);
    socket.emit('interceptLastCard', {
      gameId,
      cardIndex
    });
    setShowInterceptionPrompt(false);
    setPendingInterception(null);
  };

  const declineInterception = () => {
    if (!socket || !gameId) return;

    console.log('Declining interception');
    socket.emit('declineInterception', {
      gameId
    });
    setShowInterceptionPrompt(false);
    setPendingInterception(null);
  };

  const checkCanInterceptAce = () => {
    if (!socket || !gameId) return;

    console.log('Checking if can intercept ace');
    socket.emit('canInterceptAce', {
      gameId
    });
  };

  // Helper function to get card image path
  const getCardImage = (card: Card | null, isFaceUp: boolean = true) => {
    if (!card || !isFaceUp) {
      return process.env.PUBLIC_URL + '/assets/back.png';
    }

    // Use the image property from the card object if available
    if (card.image) {
      return process.env.PUBLIC_URL + card.image;
    }

    // Fallback: construct image path from card ID
    if (card.id) {
      const imagePath = process.env.PUBLIC_URL + `/assets/${card.id}.png`;
      console.log('Card image path:', { cardId: card.id, imagePath });
      return imagePath;
    }

    // Final fallback to back image
    console.warn('Invalid card:', card);
    return process.env.PUBLIC_URL + '/assets/back.png';
  };

  // Helper function to get overlay card image for upper bishop with selected symbol
  const getOverlayCardImage = (upperBishop: Card, selectedSymbol: string) => {
    const baseId = upperBishop.id; // e.g., "6A", "6B", "6C", "6D"
    const symbolMap = { acorn: 'A', ball: 'B', heart: 'C', leaf: 'D' };
    const selectedSymbolCode = symbolMap[selectedSymbol as keyof typeof symbolMap];
    const overlayId = `${baseId}${selectedSymbolCode}`; // e.g., "6AA", "6AB", "6AC", "6AD"
    const imagePath = `/assets/${overlayId}.png`;

    console.log('getOverlayCardImage debug:', {
      upperBishopId: upperBishop.id,
      selectedSymbol,
      selectedSymbolCode,
      overlayId,
      imagePath
    });

    return imagePath;
  };

  // Helper function to get bishop image for symbol selection
  const getBishopImageForSymbol = (symbol: 'acorn' | 'ball' | 'heart' | 'leaf') => {
    const symbolMap = { acorn: 'A', ball: 'B', heart: 'C', leaf: 'D' };
    // Use the actual played upper bishop as the base for overlay images
    const baseBishop = gameState?.lastPlayedUpperBishop?.id || '6A'; // Default to 6A if not available
    const overlayId = `${baseBishop}${symbolMap[symbol]}`; // e.g., "6AA", "6AB", "6AC", "6AD" or "6BA", "6BB", etc.
    return process.env.PUBLIC_URL + `/assets/${overlayId}.png`;
  };

  // Helper function to handle card play
  const handlePlayCard = (card: any) => {
    if (!socket || !gameId) return;

    console.log('Playing card:', card);
    socket.emit('playCard', {
      gameId,
      card: {
        id: card.id,
        value: card.value,
        symbol: card.symbol
      }
    });
  };

  if (loading) {
    return (
      <div className="game-room-layout">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading game...</p>
        </div>
      </div>
    );
  }

  if (error || !game) {
    return (
      <div className="game-room-layout">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error || 'Game not found'}</p>
          <button onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
        </div>
      </div>
    );
  }

  // Calculate playable cards based on game rules
  const playableCards = (() => {
    if (!gameState || !currentPlayer || !isMyTurn) return [];

    const playable: number[] = [];
    const topCard = gameState.faceUpDeck[gameState.faceUpDeck.length - 1];

    console.log('Playable cards calculation:', {
      isMyTurn,
      currentPlayerCards: currentPlayer.cards.map(c => ({ id: c.id, value: c.value, symbol: c.symbol })),
      topCard: topCard ? { id: topCard.id, value: topCard.value, symbol: topCard.symbol } : null,
      selectedSymbol: gameState.selectedSymbol
    });

    currentPlayer.cards.forEach((card, index) => {
      // Upper bishop can always be played
      if (card.value === 12) {
        playable.push(index);
        console.log(`Card ${card.id} (${card.symbol}) is playable: Upper bishop`);
        return;
      }

      // If there's a selected symbol, only cards of that symbol can be played
      if (gameState.selectedSymbol) {
        if (card.symbol === gameState.selectedSymbol) {
          playable.push(index);
          console.log(`Card ${card.id} (${card.symbol}) is playable: Matches selected symbol ${gameState.selectedSymbol}`);
        } else {
          console.log(`Card ${card.id} (${card.symbol}) is NOT playable: Doesn't match selected symbol ${gameState.selectedSymbol}`);
        }
        return;
      }

      // Normal card matching rules
      if (card.symbol === topCard.symbol || card.value === topCard.value) {
        playable.push(index);
        console.log(`Card ${card.id} (${card.symbol}) is playable: Matches top card ${topCard.symbol} or value ${topCard.value}`);
      } else {
        console.log(`Card ${card.id} (${card.symbol}) is NOT playable: Doesn't match top card ${topCard.symbol} or value ${topCard.value}`);
      }
    });

    console.log('Final playable cards:', playable);
    return playable;
  })();

  const mustDraw = !currentPlayer || currentPlayer.cards.length === 0 ||
    (currentPlayer.isActive && currentPlayer.cards.length > 0);

  // Get player positions with current user always at bottom
  const getPlayerPositions = (players: GamePlayer[], currentUserId: string) => {
    // Use stable positioning based on player IDs to prevent jumping
    // Create a deterministic order that doesn't change when new players join

    const positionMapping: number[] = [];

    // For 2 players: always use bottom and top positions
    if (players.length === 2) {
      // Current user always at bottom (index 0)
      const currentPlayerIndex = players.findIndex(p => p.id === currentUserId);
      const otherPlayerIndex = players.findIndex(p => p.id !== currentUserId);

      positionMapping.push(currentPlayerIndex, otherPlayerIndex);
    }
    // For 3+ players: use stable clockwise positioning based on join order
    else {
      // Current user always at bottom (index 0)
      const currentPlayerIndex = players.findIndex(p => p.id === currentUserId);
      positionMapping.push(currentPlayerIndex);

      // Add other players in the order they appear in the players array
      // This maintains the original join order and prevents position jumping
      const otherPlayers = players.filter(p => p.id !== currentUserId);

      otherPlayers.forEach(player => {
        const playerIndex = players.findIndex(p => p.id === player.id);
        positionMapping.push(playerIndex);
      });
    }

    return positionMapping;
  };

  // Get position style based on player index and total players
  const getPositionStyle = (playerIndex: number, totalPlayers: number) => {
    if (totalPlayers === 1) {
      return { bottom: '20px', left: '50%', transform: 'translateX(-50%)' };
    }

    if (totalPlayers === 2) {
      if (playerIndex === 0) {
        return { top: '465px', left: '50%', transform: 'translateX(-50%)' }; // Current user at bottom
      } else {
        return { top: '-42px', left: '50%', transform: 'translateX(-50%)' }; // Other player at top
      }
    }

    if (totalPlayers === 3) {
      switch (playerIndex) {
        case 0: return { top: '465px', left: '50%', transform: 'translateX(-50%)' }; // Current user at bottom
        case 2: return { top: '-42px', left: '50%', transform: 'translateX(-50%)' }; // Top
        case 1: return { top: '50%', right: '20px', transform: 'translateY(-50%)' }; // Right
        default: return { bottom: '20px', left: '50%', transform: 'translateX(-50%)' };
      }
    }

    if (totalPlayers === 4) {
      switch (playerIndex) {
        case 0: return { top: '465px', left: '50%', transform: 'translateX(-50%)' }; // Current user at bottom
        case 3: return { top: '-42px', left: '50%', transform: 'translateX(-50%)' }; // Top
        case 2: return { top: '50%', left: '20px', transform: 'translateY(-50%)' }; // Left
        case 1: return { top: '50%', right: '20px', transform: 'translateY(-50%)' }; // Right
        default: return { bottom: '20px', left: '50%', transform: 'translateX(-50%)' };
      }
    }

    if (totalPlayers === 5) {
      switch (playerIndex) {
        case 0: return { top: '465px', left: '50%', transform: 'translateX(-50%)' }; // Current user at bottom
        case 4: return { top: '-42px', left: '50%', transform: 'translateX(-50%)' }; // Top
        case 3: return { top: '20px', left: '20px' }; // Top-left
        case 2: return { top: '20px', right: '20px' }; // Top-right
        case 1: return { bottom: '20px', left: '20px' }; // Bottom-left
        default: return { bottom: '20px', left: '50%', transform: 'translateX(-50%)' };
      }
    }

    if (totalPlayers === 6) {
      switch (playerIndex) {
        case 0: return { top: '465px', left: '50%', transform: 'translateX(-50%)' }; // Current user at bottom
        case 5: return { top: '-42px', left: '50%', transform: 'translateX(-50%)' }; // Top
        case 4: return { top: '20px', left: '20px' }; // Top-left
        case 3: return { top: '20px', right: '20px' }; // Top-right
        case 2: return { bottom: '20px', left: '20px' }; // Bottom-left
        case 1: return { bottom: '20px', right: '20px' }; // Bottom-right
        default: return { bottom: '20px', left: '50%', transform: 'translateX(-50%)' };
      }
    }

    // Default fallback
    return { top: '465px', left: '50%', transform: 'translateX(-50%)' };
  };

  return (
    <div className="pharaon-game-room-layout">
      {/* Floating mobile controls */}
      <button
        className="game-mobile-burger"
        aria-label="Open menu"
        onClick={() => setLeftPanelCollapsed(false)}
      >
        ☰
      </button>
      <button
        className="game-mobile-info"
        aria-label="Open game details"
        onClick={() => setRightPanelCollapsed(false)}
      >
        📊
      </button>

      {/* Collapsible Left Sidebar */}
      <div className={`pharaon-game-left-panel ${leftPanelCollapsed ? 'collapsed' : ''} ${gameState?.gamePhase === 'playing' ? 'hidden-during-game' : ''}`}>
        <div className="panel-header">
          <button
            className="collapse-btn"
            onClick={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
            title={leftPanelCollapsed ? "Expand navigation" : "Collapse navigation"}
          >
            {leftPanelCollapsed ? '☰' : '✕'}
          </button>
          {!leftPanelCollapsed && <h3>{t('game.navigation')}</h3>}
        </div>

        {!leftPanelCollapsed && (
          <div className="panel-content">
            <div className="nav-section">
              <button
                className="nav-button"
                onClick={() => navigate('/dashboard')}
              >
                <span>🏠</span>
                <span>{t('game.dashboard')}</span>
              </button>

              <button
                className="nav-button"
                onClick={() => navigate('/friends')}
              >
                <span>👥</span>
                <span>Friends</span>
              </button>

              <button
                className="nav-button"
                onClick={() => navigate('/chat')}
              >
                <span>💬</span>
                <span>Chat</span>
              </button>

              <button
                className="nav-button"
                onClick={() => navigate('/invitations')}
              >
                <span>📩</span>
                <span>Invitations</span>
              </button>

              <button
                className="nav-button"
                onClick={() => navigate('/history')}
              >
                <span>📊</span>
                <span>History</span>
              </button>

              <button
                className="nav-button"
                onClick={() => navigate('/profile')}
              >
                <span>👤</span>
                <span>Profile</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Central Game Table Area */}
      <div className="pharaon-game-center-area">
        <div className="game-table-container">
          {/* Game Table */}
          <div className="game-table">
            {game.status === 'waiting' ? (
              // Waiting room - show player positions around the table
              <>
                {/* Center - Game info */}
                <div className="table-center">
                  <h4>{t('game.waitingForPlayers')}</h4>
                  <p>{game.players.length}/{game.maxPlayers} {t('game.playersJoined')}</p>
                  {isHost && (
                    <p><small>{t('game.youAreHost')}</small></p>
                  )}
                </div>

                {/* Player positions around the table */}
                {Array.from({ length: game.maxPlayers }, (_, index) => {
                  const positionStyle = getPositionStyle(index, game.maxPlayers);
                  const playerId = game.players[index];
                  const isCurrentUser = playerId === user?.id;

                  return (
                    <div
                      key={index}
                      className="pharaon-seat"
                      style={{
                        position: 'absolute',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '.3rem',
                        fontSize: '.95rem',
                        textAlign: 'center',
                        ...positionStyle
                      }}
                    >
                      {playerId ? (
                        <div className={`pharaon-seat-occupied ${isCurrentUser ? 'current-user' : ''}`}>

                          <div className="card-placeholder">
                            {Array.from({ length: 5 }).map((_, cardIndex) => (
                              <img
                                key={cardIndex}
                                src="/assets/back.png"
                                alt="Card back"
                                className="card-back"
                                style={{
                                  marginLeft: cardIndex > 0 ? '-15px' : '0'
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="pharaon-seat-empty">
                          <div className="seat-placeholder">
                            <span>Empty Seat</span>
                            {!isCurrentUser && isInRoom && !isSitting && (
                              <button
                                className="sit-here-btn"
                                onClick={() => handleSit()}
                              >
                                Sit Here
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            ) : game.status === 'playing' && gameState ? (
              // Playing state - show proper Pharaon game table
              <>
                {/* Game Table Center - Card decks and game area */}
                <div className="table-center">
                  {/* Game status */}
                  <div className="game-status-center">
                    {/* Removed redundant status indicators - they're now shown in appropriate locations */}
                  </div>
                  {/* Face-down deck (draw pile) */}
                  <div className="deck-area">
                    <div className="face-down-deck" onClick={drawCard}>
                      {gameState.faceDownDeck && gameState.faceDownDeck.length > 0 ? (
                        <div className="deck-stack">
                          <img
                            src={process.env.PUBLIC_URL + "/assets/back.png"}
                            alt="Draw deck"
                            className="deck-card"
                          />
                          <div className="deck-count">{gameState.faceDownDeck.length}</div>
                        </div>
                      ) : (
                        <div className="empty-deck">Empty</div>
                      )}
                    </div>

                    {/* Face-up deck (discard pile) */}
                    <div className="face-up-deck">
                      {gameState.faceUpDeck && gameState.faceUpDeck.length > 0 ? (
                        <div className="top-card-container">
                          <img
                            src={getCardImage(gameState.faceUpDeck[gameState.faceUpDeck.length - 1])}
                            alt="Top discard card"
                            className="deck-card top-card"
                          />
                          {/* Show selected symbol overlay for upper bishop */}
                          {gameState.selectedSymbol && gameState.faceUpDeck[gameState.faceUpDeck.length - 1]?.value === 12 && (
                            <img
                              src={getOverlayCardImage(gameState.faceUpDeck[gameState.faceUpDeck.length - 1], gameState.selectedSymbol)}
                              alt={`Selected ${gameState.selectedSymbol}`}
                              className="selected-symbol-overlay"
                            />
                          )}
                        </div>
                      ) : gameState.cutCard ? (
                        <img
                          src={getCardImage(gameState.cutCard)}
                          alt="Cut card"
                          className="deck-card top-card"
                        />
                      ) : (
                        <div className="empty-deck">No cards</div>
                      )}
                    </div>
                  </div>


                </div>

                {/* Player positions around the table */}
                {(() => {
                  const playerPositions = getPlayerPositions(gameState.players, user?.id || '');
                  return playerPositions.map((playerIndex: number) => {
                    const player = gameState.players[playerIndex];
                    const positionStyle = getPositionStyle(playerPositions.indexOf(playerIndex), gameState.players.length);
                    const isCurrentUser = player.id === user?.id;
                    const isCurrentTurn = gameState.currentPlayerIndex === playerIndex;

                    return (
                      <div
                        key={player.id}
                        className={`pharaon-seat pharaon-seat-occupied ${isCurrentUser ? 'current-user' : ''} ${isCurrentTurn ? 'current-turn' : ''}`}
                        style={{
                          position: 'absolute',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '.5rem',
                          fontSize: '.95rem',
                          textAlign: 'center',
                          ...positionStyle
                        }}
                      >
                        {/* Player cards */}
                        <div className="player-cards-area">
                          {isCurrentUser ? (
                            // Show actual cards for current user
                            <div className="user-hand">
                              {currentPlayerCards && currentPlayerCards.map((card: any, cardIndex: number) => {
                                const isPlayable = playableCards.includes(cardIndex);
                                return (
                                  <img
                                    key={`${card.id}-${cardIndex}`}
                                    src={getCardImage(card)}
                                    alt={`${card.value} of ${card.symbol}`}
                                    className={`hand-card ${isPlayable ? 'playable' : 'not-playable'} ${isCurrentTurn && isMyTurn ? 'clickable' : ''}`}
                                    style={{
                                      marginLeft: cardIndex > 0 ? '-20px' : '0',
                                      zIndex: cardIndex,
                                      transform: isPlayable ? 'translateY(-5px)' : 'none'
                                    }}
                                    onClick={() => {
                                      console.log('Card clicked:', {
                                        cardIndex,
                                        card,
                                        isCurrentTurn,
                                        isMyTurn,
                                        isPlayable,
                                        currentPlayerCards: currentPlayerCards.length
                                      });

                                      // Simplified condition - just check if it's playable
                                      if (isPlayable) {
                                        console.log('Playing card:', cardIndex);
                                        playCard(cardIndex);
                                      } else {
                                        console.log('Card not playable');
                                      }
                                    }}
                                  />
                                );
                              })}
                            </div>
                          ) : (
                            // Show card backs for other players
                            <div className="opponent-hand">
                              {Array.from({ length: player.cards?.length || 0 }).map((_, cardIndex) => (
                                <img
                                  key={cardIndex}
                                  src={process.env.PUBLIC_URL + "/assets/back.png"}
                                  alt="Hidden card"
                                  className="hand-card opponent-card"
                                  style={{
                                    marginLeft: cardIndex > 0 ? '-20px' : '0',
                                    zIndex: cardIndex
                                  }}
                                />
                              ))}
                            </div>
                          )}
                        </div>

                          {isCurrentUser ? (
                           null 
                          ) : (
                            <div className="player-info-badges">
                        <div className="seat-avatar">
                          <ProfileAvatar
                            user={{
                              id: player.id,
                              firstName: playerUsers[player.id]?.firstName || player.name?.split(' ')[0] || 'Player',
                              lastName: playerUsers[player.id]?.lastName || player.name?.split(' ')[1] || '',
                              profilePicture: playerUsers[player.id]?.profilePicture
                            }}
                            size={48}
                          />
                        </div>

                          {player.isDealer && <span className="dealer-badge">👑</span>}
                        </div>
                          )}
                          {isCurrentTurn && gameState.sevenChain > 0 && (
                            <div className="seven-chain-indicator">
                              <span>🃇 Draw {gameState.sevenChain} cards</span>
                            </div>
                          )}
                      </div>
                    );
                  });
                })()}
              </>
            ) : (
              // Game finished or other states
              <div className="table-center">
                <h4>Game Status: {game.status}</h4>
                {gameState?.winner && (
                  <p>Winner: {gameState.winner}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Board action bar - primary actions always visible on the table */}
        {game.status === 'waiting' && (
          <div className="board-action-bar">
            {!isInRoom && (
              <>
                <button
                  className="board-action-btn primary"
                  onClick={() => handleJoinRoom(false)}
                  disabled={(game.status as string) === 'playing'}
                >
                  🎮 Join Game
                </button>
                <button
                  className="board-action-btn secondary"
                  onClick={() => handleJoinRoom(true)}
                >
                  👁️ Spectate
                </button>
              </>
            )}

            {isInRoom && !isSitting && (
              <button
                className="board-action-btn primary"
                onClick={() => handleSit()}
              >
                💺 Sit at Table
              </button>
            )}

            {isHost && (
              <button
                className="board-action-btn start"
                onClick={handleStartGame}
                disabled={game.players.length < 2}
              >
                ▶️ Start Game{game.players.length < 2 ? ` (${game.players.length}/2)` : ''}
              </button>
            )}
          </div>
        )}

        {game.status === 'playing' && !isInRoom && (
          <div className="board-action-bar">
            <button
              className="board-action-btn secondary"
              onClick={() => handleJoinRoom(true)}
            >
              👁️ Spectate
            </button>
          </div>
        )}
      </div>

      {/* Right Panel - Game Actions and Details */}
      <div className={`pharaon-game-right-panel ${rightPanelCollapsed ? 'collapsed' : ''}`}>
        <div className="panel-header">
          <div className="header-left">
            <button
              className="collapse-btn"
              onClick={() => setRightPanelCollapsed(!rightPanelCollapsed)}
              title={rightPanelCollapsed ? "Expand game panel" : "Collapse game panel"}
            >
              {rightPanelCollapsed ? '🎮' : '✕'}
            </button>
          </div>
        </div>

        {!rightPanelCollapsed && (
          <div className="panel-content">

            {/* Game Controls */}
            {gameState && gameState.gamePhase === 'match-finished' && isHost && (
              <div className="info-section">
                <h4>{t('game.controls')}</h4>
                <div className="controls">
                  <button
                    className="control-btn start-match-btn"
                    onClick={handleStartNextMatch}
                  >
                    {t('game.startNextMatch')}
                  </button>
                  <button
                    className="control-btn restart-btn"
                    onClick={handleRestartGame}
                  >
                    {t('game.restartGame')}
                  </button>
                </div>
              </div>
            )}
            {/* Players */}
            <div className="info-section">
              <h4>{t('game.players')} ({game.players.length}/{game.maxPlayers})</h4>
              <div className="players-list">
                {game.players.map((playerId, index) => {
                  const gameStatePlayer = gameState?.players?.find(p => p.id === playerId);
                  const isCurrentPlayerTurn = gameState?.currentPlayerIndex === index;
                  return (
                    <div key={playerId} className={`player-item ${isCurrentPlayerTurn ? 'current-turn' : ''}`}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                        <ProfileAvatar
                          user={{
                            id: playerId,
                            firstName: playerUsers[playerId]?.firstName || gameStatePlayer?.name?.split(' ')[0] || 'Player',
                            lastName: playerUsers[playerId]?.lastName || gameStatePlayer?.name?.split(' ')[1] || '',
                            profilePicture: playerUsers[playerId]?.profilePicture
                          }}
                          size={32}
                        />
                        <div style={{ flex: 1 }}>
                          <div className="player-name">{gameStatePlayer?.name || 'Player'}</div>
                          <div className="player-badges">
                            {gameStatePlayer?.isDealer && (
                              <span className="dealer-badge">👑 Dealer</span>
                            )}
                            {isCurrentPlayerTurn && (
                              <span className="active-badge">🎯 Turn</span>
                            )}
                            {gameStatePlayer?.cards && (
                              <span className="card-count">{gameStatePlayer.cards.length} cards</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {/* Empty seats - only show when game is not playing */}
                {game.status !== 'playing' && Array.from({ length: game.maxPlayers - game.players.length }).map((_, index) => (
                  <div key={`empty-${index}`} className="player-item empty-seat">
                    <span>{t('game.emptySeat')} {game.players.length + index + 1}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Game Actions */}
            <div className="info-section">
              <h4>{t('game.actions')}</h4>
              <div className="game-actions">
                {!isInRoom && (
                  <>
                    <button
                      className="action-btn join-btn"
                      onClick={() => handleJoinRoom(false)}
                      disabled={game.status === 'playing'}
                      title={game.status === 'playing' ? 'Cannot join game in progress' : 'Join as player'}
                    >
                      <span>🎮</span>
                      <span>{game.status === 'playing' ? 'Game in Progress' : 'Join Game'}</span>
                    </button>
                    <button
                      className="action-btn spectate-btn"
                      onClick={() => handleJoinRoom(true)}
                    >
                      <span>👁️</span>
                      <span>Spectate</span>
                    </button>
                  </>
                )}

                {isInRoom && !isSitting && (
                  <button
                    className="action-btn sit-btn"
                    onClick={() => handleSit()}
                  >
                    <span>💺</span>
                    <span>Sit at Table</span>
                  </button>
                )}

                {isInRoom && (
                  <>
                    {isSpectating && (
                      <div style={{ padding: '8px', background: 'rgba(33, 150, 243, 0.1)', borderRadius: '4px', border: '1px solid rgba(33, 150, 243, 0.3)', marginBottom: '8px' }}>
                        <span style={{ color: '#2196F3', fontSize: '12px' }}>
                          👁️ You are spectating this game
                        </span>
                      </div>
                    )}
                    <button
                      className="action-btn leave-btn"
                      onClick={handleLeaveRoom}
                    >
                      <span>🚪</span>
                      <span>{t('game.leaveRoom')}</span>
                    </button>
                  </>
                )}

                {isHost && game.status === 'waiting' && (
                  <>
                    <button
                      className="action-btn start-btn"
                      onClick={handleStartGame}
                      disabled={game.players.length < 2}
                    >
                      <span>▶️</span>
                      <span>Start Game</span>
                    </button>
                    <button
                      className="action-btn delete-btn"
                      onClick={handleDeleteGame}
                    >
                      <span>🗑️</span>
                      <span>Delete Game</span>
                    </button>
                  </>
                )}

                {/* Game Controls during play */}
                {game.status === 'playing' && isSitting && isMyTurn && (
                  <>
                    <button
                      className="action-btn draw-btn"
                      onClick={drawCard}
                      disabled={!mustDraw}
                    >
                      <span>🃏</span>
                      <span>Draw Card</span>
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Player Scores */}
            {gameState && gameState.gamePhase !== 'waiting' && (
              <div className="info-section">
                <h4>Scores</h4>
                <div className="scores-list">
                  {gameState.players.map(player => (
                    <div key={player.id} className="score-item">
                      <span className="player-name">
                        {player.name}
                        {player.isDealer && <span className="dealer-badge">👑</span>}
                      </span>
                      <span className={`score ${(gameState.playerScores[player.id] || 0) >= 100 ? 'losing' : ''}`}>
                        {gameState.playerScores[player.id] || 0}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Game Status */}
            <div className="info-section">
              <h4>{t('game.status')}</h4>
              <div className="status-info">
                <div className="status-item">
                  <span>Game:</span>
                  <span className={`status-badge ${game.status}`}>{t(`dashboard.${game.status}`)}</span>
                </div>
                {gameState && (
                  <>
                    <div className="status-item">
                      <span>{t('game.phase')}:</span>
                      <span className="phase-badge">{gameState.gamePhase}</span>
                    </div>
                    <div className="status-item">
                      <span>{t('game.mode')}:</span>
                      <span className={`mode-badge ${gameState.gameMode}`}>
                        {t(`game.${gameState.gameMode}`)}
                      </span>
                    </div>
                  </>
                )}
                {game.status === 'playing' && !isInRoom && (
                  <div className="status-item" style={{ marginTop: '8px', padding: '8px', background: 'rgba(255, 193, 7, 0.1)', borderRadius: '4px', border: '1px solid rgba(255, 193, 7, 0.3)' }}>
                    <span style={{ color: '#ffc107', fontSize: '12px' }}>
                      ⚠️ Game is in progress. You can spectate to watch the game.
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Game Info */}
            <div className="info-section">
              <h4>{t('game.gameInfo')}</h4>
              <div className="info-item">
                <span>{t('game.host')}:</span>
                <span>{game.hostName || 'Unknown'}</span>
              </div>
              <div className="info-item">
                <span>{t('game.variant')}:</span>
                <span>{t('game.lowerGreen')}</span>
              </div>
              <div className="info-item">
                <span>{t('game.decks')}:</span>
                <span>{game.decks}</span>
              </div>
              <div className="info-item">
                <span>{t('game.entryFee')}:</span>
                <span>{gameState?.entryFee || 0} coins</span>
              </div>
              <div className="info-item">
                <span>{t('game.maxPlayers')}:</span>
                <span>{game.maxPlayers}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Symbol Selection Modal */}
      {showSymbolSelector && (
        <Modal
          title="Select Symbol"
          onClose={() => {}}
          hideClose
          disableBackdropClose
          maxWidth={420}
          className="symbol-selector-modal"
        >
          <div className="symbol-options">
            {(['acorn', 'ball', 'heart', 'leaf'] as const).map(symbol => (
              <button
                key={symbol}
                className="symbol-btn"
                onClick={() => selectSymbol(symbol)}
              >
                <img
                  src={getBishopImageForSymbol(symbol)}
                  alt={symbol}
                  className="symbol-image"
                />
                <span>{symbol}</span>
              </button>
            ))}
          </div>
        </Modal>
      )}

      {/* Ace Intercept Modal */}
      {showAceInterceptPrompt && canInterceptAce && (
        <Modal
          title="Intercept with Lower Leaf Bishop?"
          onClose={() => setShowAceInterceptPrompt(false)}
          maxWidth={420}
          className="ace-intercept-modal"
        >
          <p>You can intercept the ace with your Lower Leaf Bishop</p>
          <div className="modal-actions">
            {currentPlayerCards.map((card, index) => {
              if (card.value === 11 && card.symbol === 'leaf') {
                return (
                  <button
                    key={index}
                    className="intercept-btn"
                    onClick={() => interceptAce(index)}
                  >
                    <img
                      src={getCardImage(card)}
                      alt="Lower Leaf Bishop"
                      className="card-option"
                    />
                    Use Lower Leaf Bishop
                  </button>
                );
              }
              return null;
            })}
            <button
              className="skip-btn"
              onClick={() => setShowAceInterceptPrompt(false)}
            >
              Skip
            </button>
          </div>
        </Modal>
      )}

      {/* Last Card Interception Modal */}
      {showInterceptionPrompt && (
        <Modal
          title="Intercept Last Card?"
          onClose={declineInterception}
          maxWidth={420}
          className="interception-modal"
        >
          <p>A player finished the match! You can intercept with your Lower Leaf Bishop to make them draw 2 cards.</p>
          <div className="modal-actions">
            {currentPlayerCards.map((card, index) => {
              if (card.id === '5D') {
                return (
                  <button
                    key={index}
                    className="intercept-btn"
                    onClick={() => interceptLastCard(index)}
                  >
                    <img
                      src={getCardImage(card)}
                      alt="Lower Leaf Bishop"
                      className="card-option"
                    />
                    Intercept with Lower Leaf Bishop
                  </button>
                );
              }
              return null;
            })}
            <button
              className="skip-btn"
              onClick={declineInterception}
            >
              Let Match End
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default GameRoom; 